import React, { useRef, useEffect, useState, useCallback } from 'react'
import {
	Route,
	Switch,
	useHistory,
	Redirect,
	useLocation,
} from 'react-router-dom'

import navigation, { rulesPaths, eventsSubscriptionsPaths } from './navigation'
import useSession from '../../hooks/useSession'

import Homepage from '../Homepage/Homepage'
import ClinicalProcessController from '../ClinicalProcess/ClinicalProcessController'
import ClinicalProcessesController from '../ClinicalProcesses/ClinicalProcessesController'
import ContextualController from '../ContextualController/ContextualController'
import TaskManager from '../TaskManager/TaskManager'
import ErrorBoundary from 'components/general/ErrorBoundary/ErrorBoundary'
import SearchInput from 'components/SearchInput/SearchInput'
import UsersController from 'controllers/UsersController/UsersController'
import UserController from 'controllers/UsersController/UserController'

import * as dialogs from 'dialogs'
import { i18n } from 'localization'

import { GlobalProvider } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import AsideMenu from '@jimmyboys/liita-ui/dist/components/AsideMenu'
import TabBar from '@jimmyboys/liita-ui/dist/components/TabBar'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import FlexAppHeader from '@jimmyboys/liita-ui/dist/components/FlexAppHeader'
import RouteTransition from '@jimmyboys/liita-ui/dist/components/RouteTransition'
import DialogManagerV2 from '@jimmyboys/liita-ui/dist/components/DialogManagerV2'
import NotificationManager from '@jimmyboys/liita-ui/dist/components/NotificationManager'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import useMediaQuery from 'hooks/useMediaQuery'

import styles from './App.module.sass'
import theme from 'assets/styles/theme.module.sass'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import Notifications from 'controllers/Notifications/Notifications'
import useNotificationService from 'hooks/useNotificationService'
import { useReload } from 'store'
import SettingsController from 'controllers/SettingsController/SettingsController'
import SettingsNavigation from 'controllers/Settings/SettingsNavigation/SettingsNavigation'
import CoordinatorAllocationRules from 'controllers/CoordinatorAllocationRules/CoordinatorAllocationRules'
import TasksController from 'controllers/Tasks/TasksController'
import RulesEditorController from 'controllers/Settings/RulesEditorController/RulesEditorController'
import EventsSubscriptionsController from 'controllers/Settings/RulesEditorController/EventsSubscriptionsController'
import useTaskService from 'hooks/useTaskService'
import queryString from 'query-string'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import ContextualActionsController from 'controllers/ContextualController/ContextualActionsController'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Empty from 'components/Empty/Empty'
import { useRecoilValue } from 'recoil'

import AsideHeader from 'components/AsideHeader/AsideHeader'
import InboxStateController from '../InboxController/InboxStateController'
import InboxController from 'controllers/InboxController/InboxController'
import { expandedInboxState } from '../../store/atoms'
import { DrawerProvider } from 'context/Drawer/DrawerContext'
import { API_URL, fetchOptions } from 'store/fetchUtils'
import StatusController from 'controllers/Status/StatusController'
import PersonalAreaController from 'controllers/PersonalArea/PersonalAreaController'
import ProtocolsAreaController from 'controllers/Protocols/ProtocolsAreaController'
import { presetProtocolsPaginationRequestsUri } from 'controllers/Protocols/PresetProtocols/PresetProtocolsController'
import MissingTasksController from 'controllers/Tasks/MissingTasksController/MissingTasksController'

const APP_VERSION = window.REACT_APP_VERSION || process.env.REACT_APP_VERSION

const App = () => {
	const { user, logout } = useSession()
	const history = useHistory()
	const location = useLocation()
	const { pathname } = location
	const mainRef = useRef(null)
	const scrollInfoRef = useRef({})

	const [userLoggedOut, setUserLoggedOut] = useState(false)

	const { markAsRead, markAsUnread, markAsClosed } = useNotificationService()
	const { cancelTask } = useTaskService()
	const reload = useReload()
	const [queryParams, , setSearchParams] = useQueryParams(location)
	const qs = queryString.stringify(queryParams)

	const leftSideRef = useRef(null)
	const rightSideRef = useRef(null)

	const dialogManager = useRef(null)
	const notificationManager = useRef(null)

	const inboxControllerRef = useRef()
	const expandedInbox = useRecoilValue(expandedInboxState)

	const breakpoints = {
		small: theme.smallBreakpoint,
		large: theme.largeBreakpoint,
		larger: theme.largerBreakpoint,
	}
	const screenSize = useMediaQuery(breakpoints)

	const DEFAULT_PAGE_SIZE = 48

	const isAdmin = user?.roles?.some((role) => role.toLowerCase() === 'admin')
	const isManager = user?.roles?.some(
		(role) => role.toLowerCase() === 'manager'
	)

	const options = [
		{
			value: 'users',
			text: i18n`Users`,
			action: () => {
				history.push(`/users/`)
			},
		},
		{
			value: 'settings',
			text: i18n`Settings`,
			action: () => {
				history.push(`/settings-v2/`)
			},
		},
		{
			value: 'logout',
			text: i18n`Logout`,
			action: () => {
				setUserLoggedOut(true)
				logout()
			},
		},
	].filter((f) => f)

	useEffect(() => {
		if (!mainRef.current) return
		const handleClick = () => {
			scrollInfo[pathname] = mainRef.current.scrollTop
		}
		window.addEventListener('click', handleClick, true)

		const scrollInfo = scrollInfoRef.current
		if (window.history.scrollRestoration) {
			window.history.scrollRestoration = 'manual'
		}
		if (history.action === 'POP' && scrollInfo[pathname]) {
			mainRef.current.scrollTo({ top: scrollInfo[pathname] })
		} else {
			mainRef.current.scrollTo({ top: 0 })
		}
		return () => {
			window.removeEventListener('click', handleClick, true)
		}
	}, [pathname, history])

	useEffect(() => {
		if (!user) {
			logout()
		}
	}, [user, logout])

	const updateNotifications = (clinicalProcessId) => {
		if (location.pathname === '/notifications/') {
			reload([`/notifications${qs ? `?${qs}` : ''}`])
		} else {
			reload([`/notifications`])
		}

		if (clinicalProcessId) {
			reload([`/clinical_processes/${clinicalProcessId}/notifications`])
		}

		inboxControllerRef.current?.reloadInbox()
	}

	const appFunctions = {
		showDialog(
			type,
			title,
			data,
			dockable,
			icon,
			startingPoint = 'center',
			id,
			scrollable = true,
			closeBackdropOnClick = false
		) {
			return dialogManager.current.pushDialog(
				type,
				title,
				data,
				dockable,
				id,
				icon,
				startingPoint,
				scrollable,
				closeBackdropOnClick
			)
		},
		openFile: useCallback((file, backdrop) => {
			dialogManager.current
				.pushDialog(
					'FileDialog',
					file.name || file.type?.name || i18n`File viewer`,
					file,
					false,
					'file-viewer',
					undefined,
					'center',
					false,
					null,
					backdrop
				)
				.then(() => {})
				.catch(() => {})
				.finally(() => {})
		}, []),
		clearDialogs() {
			dialogManager.current.clearDialogs()
		},
		closeInbox() {
			inboxControllerRef.current?.setExpandedInbox(false)
		},
		markNotificationAsRead(notificationId, clinicalProcessId) {
			markAsRead(notificationId)
				.then(() => {
					updateNotifications(clinicalProcessId)
				})
				.catch(console.error)
				.finally(() => {})
		},
		markNotificationAsUnread(notificationId, clinicalProcessId) {
			markAsUnread(notificationId)
				.then(() => {
					updateNotifications(clinicalProcessId)
				})
				.catch(console.error)
				.finally(() => {})
		},
		closeNotification(notificationId, clinicalProcessId) {
			markAsClosed(notificationId).then(() => {
				updateNotifications(clinicalProcessId)
				inboxControllerRef.current?.setForceInboxUpdate(true)
				notificationManager.current.pushNotification(
					undefined,
					'success',
					i18n`Notification closed!`
				)
			})
		},
		postponeNotification(notificationId, clinicalProcessId) {
			dialogManager.current
				.pushDialog(
					'PostponeNotification',
					i18n`Postpone Notification` + ` #${notificationId}`,
					{
						notificationId,
					},
					false,
					'postpone-notification-dialog',
					undefined,
					'center',
					undefined,
					false
				)
				.then(() => {
					updateNotifications(clinicalProcessId)
					inboxControllerRef.current?.setForceInboxUpdate(true)
					notificationManager.current.pushNotification(
						undefined,
						'success',
						i18n`Notification postponed!`
					)
				})
				.catch(() => null)
				.finally(() => {})
		},
		replyNotification(clinicalProcessId, commentId) {
			dialogManager.current
				.pushDialog(
					'CommentDialog',
					i18n`Comment Dialog`,
					{
						processId: clinicalProcessId,
						commentId,
					},
					false,
					'comment-dialog',
					undefined,
					'center',
					undefined,
					false
				)
				.then(() => {
					reload([])
				})
				.catch(() => null)
				.finally(() => {})
		},
		addNote({ processId, contentUri }) {
			dialogManager.current
				.pushDialog(
					'NoteDialog',
					i18n`Note Dialog`,
					{
						processId,
						contentUri,
					},
					false,
					'note-dialog',
					undefined,
					'center',
					undefined,
					false
				)
				.then(() => {
					const uriQueryParams = contentUri?.split('?')?.[1]

					reload([
						contentUri && `${contentUri}`,
						uriQueryParams && `/notes?${uriQueryParams}`,
						processId && `/notes/clinical_process/${processId}`,
						`/notes/today_reminders?claimsManagerTrust=${user.legacyId}`,
					])
				})
				.catch(() => null)
				.finally(() => {})
		},
		updateNote({ note, processId, contentUri }) {
			dialogManager.current
				.pushDialog(
					'NoteDialog',
					i18n`Note Dialog`,
					{
						note,
						processId,
					},
					false,
					'note-dialog',
					undefined,
					'center',
					undefined,
					false
				)
				.then(() => {
					const uriQueryParams = contentUri?.split('?')?.[1]

					reload([
						contentUri && `${contentUri}`,
						uriQueryParams && `/notes?${uriQueryParams}`,
						processId && `/notes/clinical_process/${processId}`,
						`/notes/today_reminders?claimsManagerTrust=${user.legacyId}`,
					])
				})
				.catch(() => null)
				.finally(() => {})
		},
		deleteNote({ note, processId, contentUri }) {
			dialogManager.current
				.pushDialog(
					'ConfirmDialog',
					i18n`Delete note`,
					{
						user,
						label: i18n`Are you sure you want to delete this note?`,
					},
					false,
					'confirm-dialog',
					null,
					'center',
					undefined,
					false
				)
				.then(() => {
					const url = `${API_URL}/api/notes${
						note ? `/${note?.id}` : ''
					}`

					fetch(
						url,
						fetchOptions({
							method: 'DELETE',
						})
					)
						.then(() => {
							notificationManager.current.pushNotification(
								undefined,
								'success',
								i18n`Note deleted successfully!`
							)

							const uriQueryParams = contentUri?.split('?')?.[1]

							reload([
								contentUri && `${contentUri}`,
								uriQueryParams && `/notes?${uriQueryParams}`,
								processId &&
									`/notes/clinical_process/${processId}`,
								`/notes/today_reminders?claimsManagerTrust=${user.legacyId}`,
							])
						})
						.catch((err) => {
							if (err.status === '401' || err.status === 401) {
								this.expireSession()
							}

							notificationManager.current.pushNotification(
								undefined,
								'error',
								err.message
							)
						})
				})
				.catch(() => null)
				.finally(() => {})
		},
		deletePresetProtocol({ bodyPart, injury, acutePhase }) {
			dialogManager.current
				.pushDialog(
					'ConfirmDialog',
					i18n`Delete Preset Protocol`,
					{
						user,
						label: i18n`Are you sure you want to delete this preset protocol?`,
					},
					false,
					'confirm-dialog',
					null,
					'center',
					undefined,
					false
				)
				.then(() => {
					const url = `${API_URL}/api/rules/protocols_presets/body_part/${bodyPart}/injury/${injury}/acute_phase/${acutePhase}`

					fetch(
						url,
						fetchOptions({
							method: 'DELETE',
						})
					)
						.then(() => {
							notificationManager.current.pushNotification(
								undefined,
								'success',
								i18n`Preset protocol deleted successfully!`
							)

							const pagesRequests =
								presetProtocolsPaginationRequestsUri(
									queryParams
								)

							reload(pagesRequests)
						})
						.catch((err) => {
							if (err.status === '401' || err.status === 401) {
								this.expireSession()
							}

							notificationManager.current.pushNotification(
								undefined,
								'error',
								err.message
							)
						})
				})
				.catch(() => null)
		},
		cancelTask(data) {
			dialogManager.current
				.pushDialog(
					'PromptDialog',
					i18n`Cancel Task`,
					{
						label: i18n`Are you sure you want to cancel the task?`,
					},
					false,
					'cancel-task-dialog',
					null,
					'center',
					undefined,
					false
				)
				.then((justification) => {
					cancelTask(
						data,
						justification,
						i18n,
						notificationManager.current.pushNotification
					).then(() => {
						const pageList = new Array(
							parseInt(queryParams.page, 10) || 1
						)
						const pagesRequests = pageList
							.fill(null)
							.map((_, index) => {
								const pageQueryParams = {
									...queryParams,
									page: index,
									pageSize: DEFAULT_PAGE_SIZE,
								}
								const qs =
									queryString.stringify(pageQueryParams)
								const pageUrl = `/tasks?${qs || ''}`
								return pageUrl
							})

						reload(pagesRequests)
					})
				})
				.catch((e) =>
					notificationManager.current.pushNotification(
						undefined,
						'error',
						e.message
					)
				)
				.finally(() => {})
		},
		pushNotification(id, type, message, displayTime, hideProgressBar) {
			notificationManager.current.pushNotification(
				id,
				type,
				message,
				displayTime,
				hideProgressBar
			)
		},
		gotoProcess(processId) {
			history.push(`/clinical-processes/${processId}/appointments`)
		},
		goTo(link) {
			history.replace(link)
		},
		expireSession() {
			logout()
			setSearchParams({
				redirect: `${location.pathname}`,
			})
		},
		gotoAppointment(processId, appointmentId) {
			if (history.location.hash !== `#C_${appointmentId}`) {
				history.push(
					`/clinical-processes/${processId}/appointments${history.location.search}#C_${appointmentId}`
				)
			}
		},
		gotoRequisition(processId, requisitionId) {
			if (history.location.hash !== `#R_${requisitionId}`) {
				history.push(
					`/clinical-processes/${processId}/requisitions${history.location.search}#R_${requisitionId}`
				)
			}
		},
		forwardNotification(notificationId, clinicalProcessId) {
			dialogManager.current
				.pushDialog(
					'ConfirmDialog',
					i18n`Forward notification to manager`,
					{
						label: i18n`Are you sure you want to forward this notification to the manager?`,
					},
					false,
					'confirm-dialog',
					null,
					'center',
					undefined,
					false
				)
				.then(() => {
					const url = `${API_URL}/api/notifications/${notificationId}/forward`

					let formData = {
						processId: +clinicalProcessId,
					}

					fetch(
						url,
						fetchOptions({
							method: 'PUT',
							body: formData,
						})
					)
						.then(() => {
							updateNotifications(clinicalProcessId)
							inboxControllerRef.current?.setForceInboxUpdate(
								true
							)
							notificationManager.current.pushNotification(
								undefined,
								'success',
								i18n`Notification forwarded successfully!`
							)
						})
						.catch((err) => {
							if (err.status === '401' || err.status === 401) {
								this.expireSession()
							}

							notificationManager.current.pushNotification(
								undefined,
								'error',
								err.message
							)
						})
				})
				.catch(() => null)
		},
	}

	if (!user) {
		const searchParams =
			!userLoggedOut && location.pathname !== '/'
				? `?redirect=${location.pathname}`
				: ''
		return <Redirect to={`/login${searchParams}`} />
	}

	return (
		<GlobalProvider
			value={{
				...appFunctions,
				i18n,
				screenSize,
			}}
		>
			<InboxStateController ref={inboxControllerRef} />
			{screenSize !== 'small' && (
				<AsideMenu
					className={`${
						inboxControllerRef.current?.disableInbox
							? styles['disable-inbox']
							: ''
					}`}
					routes={navigation(i18n, isAdmin, isManager)}
					user={user || undefined}
					data-wt-id='menu'
					selectionStyle='highlight'
					onClick={(e) => {
						if (e.target.classList.contains('avatar')) {
							history.push(`/users/${user.id}/user-exclusion/`)
						}

						if (e.target.parentNode?.id) {
							inboxControllerRef.current?.setExpandedInbox(
								(prev) => !prev
							)
						} else {
							inboxControllerRef.current?.setExpandedInbox(false)
						}
					}}
				>
					<button
						data-wt-id='logout'
						className={styles['menu-button']}
						onClick={() => {
							setUserLoggedOut(true)
							logout()
						}}
					>
						<Icon name='logout' />
						<small>{i18n`Logout`}</small>
					</button>
					<small className={styles['version']}>{APP_VERSION}</small>
				</AsideMenu>
			)}
			{screenSize === 'small' && (
				<TabBar
					routes={navigation(i18n, isAdmin, isManager, 4)}
					data-wt-id='menu'
					onClick={(e) => {
						const parent = e.target.closest('span')

						if (parent?.id) {
							inboxControllerRef.current?.setExpandedInbox(
								(prev) => !prev
							)
						} else {
							inboxControllerRef.current?.setExpandedInbox(false)
						}
					}}
				>
					<ModalOptions
						title={APP_VERSION}
						options={options}
						onSelect={(item, e) => {
							item && item.action && item.action()
						}}
					>
						<button
							data-wt-id='options'
							className={styles['menu-button']}
						>
							<Icon width='24px' height='24px' name='options' />
							<small>{i18n`Options`}</small>
						</button>
					</ModalOptions>
				</TabBar>
			)}
			<Empty
				elementRef={leftSideRef}
				contentClassName={styles['panels']}
				startEmpty={true}
			>
				{(isEmpty) => (
					<aside
						ref={leftSideRef}
						data-wt-id='aside'
						className={`${styles['left-aside']} ${
							isEmpty ? styles['is-empty'] : ''
						}`}
					>
						<ErrorBoundary render={() => null}>
							<DrawerProvider>
								<AsideHeader />
								<Widget
									className={styles['left-content']}
									paddings={['small', 'small', '']}
								>
									<RouteTransition
										animation='fade'
										className={styles['panels']}
									>
										<Switch>
											{expandedInbox && (
												<Route path='*'>
													<InboxController
														isVisible={
															expandedInbox
														}
														className={`${styles['inbox']}`}
													/>
												</Route>
											)}
											<Route path='/users' exact>
												<UsersController />
											</Route>
											{screenSize !== 'small' && (
												<Route path='/users/:userId'>
													<UsersController />
												</Route>
											)}
											{isAdmin && (
												<Route path='/settings' exact>
													<SettingsController />
												</Route>
											)}
											{isAdmin && (
												<Route
													path='/settings-v2'
													exact={
														screenSize === 'small'
													}
												>
													<SettingsNavigation />
												</Route>
											)}
											{isAdmin &&
												screenSize !== 'small' && (
													<Route
														path='/settings/coordinator-allocation-rules'
														exact
													>
														<SettingsController />
													</Route>
												)}
										</Switch>
									</RouteTransition>
								</Widget>
							</DrawerProvider>
						</ErrorBoundary>
					</aside>
				)}
			</Empty>
			<div className={styles['main-content']}>
				{!(screenSize === 'small' && expandedInbox) && (
					<DrawerProvider>
						<FlexAppHeader className={styles['header']} sticky>
							<ErrorBoundary render={() => null}>
								<Switch>
									<Route path='/clinical-processes/:clinicalProcessId'>
										<ContextualActionsController />
									</Route>
									<Route>
										<div />
									</Route>
								</Switch>
								<HStack align='right' gap='xsmall'>
									<SearchInput />
								</HStack>
							</ErrorBoundary>
						</FlexAppHeader>
					</DrawerProvider>
				)}
				<Empty
					elementRef={rightSideRef}
					contentClassName={styles['panels']}
				>
					{(isEmpty) => (
						<aside
							ref={rightSideRef}
							data-wt-id='aside'
							className={`${styles['right-side']} ${
								isEmpty ? styles['is-empty'] : ''
							}`}
						>
							<ErrorBoundary render={() => null}>
								<Widget
									className={styles['panels-container']}
									paddings={['', 'normal']}
								>
									<RouteTransition
										animation='fade'
										className={styles['panels']}
									>
										<Switch>
											<DrawerProvider>
												<ContextualController />
											</DrawerProvider>
										</Switch>
									</RouteTransition>
								</Widget>
							</ErrorBoundary>
						</aside>
					)}
				</Empty>
				<main ref={mainRef} className={styles['main']}>
					<ErrorBoundary>
						<RouteTransition
							animation='fade'
							adjustLeaveTopPosition
						>
							<DrawerProvider>
								<Switch>
									<Route path='/' exact>
										<Homepage />
									</Route>
									<Route path='/clinical-processes/:clinicalProcessId'>
										<ClinicalProcessController />
									</Route>
									<Route path='/clinical-processes' exact>
										<ClinicalProcessesController />
									</Route>
									<Route path='/notifications'>
										<Notifications />
									</Route>
									<Route path='/users/:userId'>
										<UserController />
									</Route>
									<Route path='/tasks' exact>
										<TasksController />
									</Route>
									<Route path='/personal-area' exact>
										<PersonalAreaController />
									</Route>
									{isAdmin && (
										<Route path='/protocols' exact>
											<ProtocolsAreaController />
										</Route>
									)}
									{isAdmin && (
										<Route path='/settings' exact>
											<div></div>
										</Route>
									)}
									{isAdmin && (
										<Route
											path='/settings/coordinator-allocation-rules'
											exact
										>
											<CoordinatorAllocationRules />
										</Route>
									)}
									{isAdmin && (
										<Route path='/settings-v2' exact>
											<div></div>
										</Route>
									)}
									{isAdmin && (
										<Route path={rulesPaths} exact>
											<RulesEditorController />
										</Route>
									)}
									{isAdmin && (
										<Route
											path={eventsSubscriptionsPaths}
											exact
										>
											<EventsSubscriptionsController />
										</Route>
									)}
									{isAdmin && (
										<Route path='/status' exact>
											<StatusController />
										</Route>
									)}
									{isAdmin && (
										<Route path='/missing-tasks' exact>
											<MissingTasksController />
										</Route>
									)}
									<Route>
										<Widget
											applyMaxWidth
											paddings={['normal', 'normal']}
										>
											<h1>404</h1>
										</Widget>
									</Route>
								</Switch>
							</DrawerProvider>
						</RouteTransition>
					</ErrorBoundary>
				</main>
			</div>
			<ErrorBoundary render={() => null}>
				<TaskManager />
			</ErrorBoundary>
			<DialogManagerV2
				ref={dialogManager}
				dialogs={dialogs}
				errorBoundary={ErrorBoundary}
			/>
			<NotificationManager ref={notificationManager} displayTime={3000} />
			{/* <WalkThrough
				show={showWalkThrough}
				items={walkThroughItems}
				onEnd={() => {
					// TODO: set [stage] preference to false
					setShowWalkThrough(() => 'false')
				}}
			/> */}
		</GlobalProvider>
	)
}

export default App
